export type Volume = {
  number: string
  title: string
  classes: string
  subtitle: string
  problem: string[]
  extraList?: { label: string; items: string[] }
  listLabel: string
  bullets: string[]
  flow?: { label?: string; steps: string[] }
  highlight?: { label?: string; items: string[] }
  result: string[]
}

export const volumes: Volume[] = [
  {
    number: 'VOLUME 1',
    title: 'Beginner Barre',
    classes: '25 Ready-to-Teach Classes',
    subtitle: 'Build Strong Barre Foundations Before Adding More Complexity',
    problem: [
      'A beginner class should not feel like a watered-down advanced workout.',
      'New students need clear positioning, manageable ranges, simple transitions and enough repetition to understand what their bodies are supposed to do.',
      'This volume gives you 25 complete beginner Barre classes ready to teach, built around the movements and alignment foundations students need before progressing.',
    ],
    listLabel: 'Inside:',
    bullets: [
      'Barre setup and body alignment',
      'Parallel position',
      'First position',
      'Second position',
      'Beginner pliés',
      'Relevés',
      'Basic glute work',
      'Introductory thigh sequences',
      'Basic core activation',
      'Balance fundamentals',
      'Controlled leg lifts',
      'Simple arm integration',
      'Beginner-friendly transitions',
      'Easier modifications',
      'Instructor cues',
      'Common corrections',
      'Full beginner class structures',
    ],
    highlight: {
      label: '100% Visual Core Format — See:',
      items: [
        'where the feet go',
        'where the knees track',
        'how the hips should be positioned',
        'how the movement begins',
        'how the movement progresses',
        'how to simplify it',
      ],
    },
    result: [
      'You can welcome newer students with a class that already has structure, progression and appropriate difficulty.',
    ],
  },
  {
    number: 'VOLUME 2',
    title: 'Glutes, Thighs & Lower Body',
    classes: '25 Ready-to-Teach Classes',
    subtitle: 'Stop Repeating the Same Lower-Body Barre Sequence Every Week',
    problem: [
      'Glutes and thighs are central to Barre.',
      'But repeatedly using the same pliés, pulses and leg lifts quickly makes classes feel predictable.',
      'This volume gives you 25 complete lower-body Barre classes, built around different objectives, equipment options, tempos and movement structures.',
    ],
    listLabel: 'Inside:',
    bullets: [
      'Glute-focused Barre',
      'Thigh endurance',
      'Inner-thigh sequences',
      'Outer-thigh sequences',
      'Glute medius work',
      'Calves and relevé',
      'Single-leg work',
      'Seat work',
      'Mini-band classes',
      'Barre ball classes',
      'Pulse and hold sequences',
      'Slow-control classes',
      'Lower-body circuits',
      'Lower-body intervals',
      'Standing Barre',
      'Barre + mat programming',
      'Balance integration',
      'Modifications',
      'Level-up options',
      'Common errors and corrections',
    ],
    flow: {
      label: 'Visual Teaching Advantage — See:',
      steps: ['Start', 'Lift', 'Pulse', 'Hold', 'Lower'],
    },
    highlight: {
      items: [
        'See correct vs incorrect positioning.',
        'See working-side direction.',
        'See modifications.',
        'See progression.',
        'See the complete class timeline.',
      ],
    },
    result: [
      'You get far more lower-body variety without having to reinvent your programming every week.',
    ],
  },
  {
    number: 'VOLUME 3',
    title: 'Core, Balance & Stability',
    classes: '25 Ready-to-Teach Classes',
    subtitle: 'Teach Control, Stability and Balance With a Clear Progression',
    problem: [
      'Barre is not just about feeling the burn.',
      'Strong students need control.',
      'They need to stabilize the pelvis, engage the core, maintain posture and progress safely into more demanding balance work.',
      'This volume gives you 25 ready-to-teach classes focused on core control, balance and stability.',
    ],
    listLabel: 'Inside:',
    bullets: [
      'Deep-core activation',
      'Standing core work',
      'Floor-based Barre core',
      'Oblique emphasis',
      'Pelvic stability',
      'Single-leg balance',
      'Weight-transfer control',
      'Core + Barre combinations',
      'Slow-control sequences',
      'Stability challenges',
      'Balance progressions',
      'Reduced-support options',
      'Beginner modifications',
      'More advanced progressions',
      'Postural cues',
      'Common balance errors',
      'Instructor corrections',
    ],
    result: [
      'Your students develop stronger control instead of simply trying to copy more difficult positions.',
    ],
  },
  {
    number: 'VOLUME 4',
    title: 'Arms, Upper Body & Posture',
    classes: '25 Ready-to-Teach Classes',
    subtitle:
      'Give the Upper Body the Same Structure as the Rest of Your Barre Class',
    problem: [
      'Upper-body blocks often become repetitive or feel disconnected from the rest of a Barre session.',
      'This volume provides 25 structured Barre classes integrating arms, shoulders, back, posture and upper-body endurance into the Barre format.',
    ],
    listLabel: 'Inside:',
    bullets: [
      'Barre arms without equipment',
      'Light-weight arm sequences',
      'Shoulder endurance',
      'Back-focused work',
      'Triceps sequences',
      'Biceps combinations',
      'Arm pulses',
      'Postural endurance',
      'Upper-body + lower-body combinations',
      'Arms + core',
      'Controlled tempo',
      'Modification strategies',
      'Progressions',
      'Alignment cues',
      'Common errors',
    ],
    result: [
      'You stop treating upper-body work like a random five-minute add-on and start integrating it into a complete Barre class.',
    ],
  },
  {
    number: 'VOLUME 5',
    title: 'Cardio & Full-Body Barre',
    classes: '25 Ready-to-Teach Classes',
    subtitle: 'Add Energy Without Turning Your Barre Class Into HIIT',
    problem: [
      'A more dynamic Barre class should still feel like Barre.',
      'It should retain control, alignment, precision and intentional movement.',
      "This volume provides 25 ready-to-teach dynamic Barre classes designed to create more energy and variety while maintaining the discipline's identity.",
    ],
    listLabel: 'Inside:',
    bullets: [
      'Low-impact Cardio Barre',
      'Full-body Barre',
      'Power-focused Barre',
      'Tempo-based sequences',
      'Controlled intervals',
      'Standing full-body flows',
      'Arms + legs combinations',
      'Core + cardio combinations',
      'Muscular-endurance classes',
      'Athletic Barre variations',
      'Express sessions',
      'Full-body finishers',
      'Modifications',
      'Intensity adjustments',
    ],
    result: [
      'You can offer more energetic classes without losing the control and precision that make Barre Barre.',
    ],
  },
  {
    number: 'VOLUME 6',
    title: 'Progressive Barre Class Series',
    classes: '25 Ready-to-Teach Classes',
    subtitle: 'Stop Teaching Isolated Classes and Start Creating Real Progression',
    problem: [
      'Your students should not simply attend random workouts.',
      'They should feel that their strength, control, endurance and confidence are moving forward.',
      'This volume gives you 25 progressive Barre classes, designed to help you gradually increase challenge over time.',
    ],
    extraList: {
      label: 'Choose According To:',
      items: [
        'current student level',
        'movement confidence',
        'balance ability',
        'muscular endurance',
        'class objective',
        'equipment available',
        'progression stage',
      ],
    },
    listLabel: 'Classes Develop:',
    bullets: [
      'foundational control',
      'stronger plié work',
      'longer holds',
      'controlled pulses',
      'reduced Barre support',
      'improved balance',
      'more demanding combinations',
      'greater muscular endurance',
      'more complex transitions',
      'progressive full-body integration',
    ],
    flow: { steps: ['Open', 'Choose', 'Teach', 'Progress'] },
    result: [
      'Instead of asking yourself, “What should come next?”, you already have a progression framework ready to use.',
    ],
  },
]
